/* globals request */
import $q from 'q';
import http from '../services/http';
import ConfigStorage from '../configs';
import API_URL_MAP from '../configs/url-api';
import {USER_ROLE} from '../constants/user';

const {API_HOST} = ConfigStorage().API_SETTINGS;

export default class User {
  static login(username, password) {
    const body = {
      username,
      password,
      grant_type: 'password',
    };
    return request(API_URL_MAP.OAUTH, {
      method: 'POST',
      body,
      auth: 'Client',
    });
  }

  static registration(data, sendEmail) {
    const body = {
      name: `${data.firstName} / ${data.lastName} / ${data.middleName}`,
      authorityLevel: 2,
      phoneNumber: data.phone,
      role: data.role,
      client_id: 'default',
      client_secret: 'default',
      ...(data.password && {password: data.password}),
      ...(data.emailVerified && {emailVerified: data.emailVerified}),
    };

    if (data.role) {
      body.role = data.role;
    }

    if (data.authorityLevel) {
      body.authorityLevel = data.authorityLevel;
    }

    if (data.role === USER_ROLE.EMPLOYER || data.role === USER_ROLE.RECRUITER) {
      body.company = {
        type: data.type || '',
        name: data.companyName || '',
        website: data.website || '',
        countOfEmployees: data.countOfEmployees || '',
        city: data.city || '',
      };
    }

    if (data.role === USER_ROLE.ADMINISTRATOR) {
      body.authorityLevel = 9;
    }

    if (sendEmail) {
      body.sendEmailPassword = true;
    }

    return request(API_URL_MAP.USERS + API_URL_MAP.REGISTRATION + data.email, {
      method: 'PUT',
      body,
      auth: 'Client',
    });
  }

  static verifyToken(token) {
    const body = {
      username: token,
    };

    return request(API_URL_MAP.USERS + API_URL_MAP.REGISTRATION + token, {
      method: 'POST',
      auth: 'Client',
      body,
    });
  }

  static refreshToken(refreshToken) {
    return request(API_URL_MAP.OAUTH, {
      method: 'POST',
      auth: 'Client',
      body: {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      },
    });
  }

  static verifyEmail(token) {
    return request(API_URL_MAP.VERIFY_EMAIL + token, {
      method: 'POST',
      auth: 'Client',
    });
  }

  static parseToken(token) {
    return request(API_URL_MAP.USERS_ME, {
      method: 'GET',
      auth: 'none',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  static restorePassword(email) {
    const body = {
      username: email,
    };

    return request(API_URL_MAP.RESTORE_PASSWORD, {
      method: 'POST',
      auth: 'Client',
      body,
    });
  }

  static sendRestorationCode(email) {
    return request(API_URL_MAP.RESTORE_PASSWORD, {
      method: 'POST',
      auth: 'Client',
      body: {
        username: email,
        client_id: 'default',
        client_secret: 'default',
      },
    });
  }

  static resetUserPassword(token, newPassword) {
    const url = `${API_URL_MAP.RESET_PASSWORD}/${token}`;
    return request(url, {
      method: 'POST',
      auth: 'Client',
      body: {
        newPassword,
        client_id: 'default',
        client_secret: 'default',
      },
    });
  }

  static logout() {
    return request(API_URL_MAP.LOGOUT, {
      method: 'POST',
      auth: 'Bearer',
    });
  }

  static removeUser(userId = 'me') {
    return request(`${API_URL_MAP.USERS}/${userId}`, {
      method: 'DELETE',
      auth: 'Bearer',
    });
  }

  static updateUser(data = {}, userId = 'me') {
    const body = {
      ...data,
    };

    return request(`${API_URL_MAP.USERS}/${userId}`, {
      method: 'PATCH',
      auth: 'Bearer',
      body,
    });
  }

  static getUserInfo(userId = 'me') {
    return request(`${API_URL_MAP.USERS}/${userId}`, {
      method: 'GET',
      auth: 'Bearer',
    });
  }

  static updateUserAvatar(photoId) {
    const body = {
      photoId,
    };
    return request(API_URL_MAP.UPDATE_USER_AVATAR, {
      method: 'PATCH',
      auth: 'Bearer',
      body,
    });
  }

  static removeUserFile(fileId) {
    return request(`${API_URL_MAP.FILES}/${fileId}`, {
      method: 'DELETE',
      auth: 'Bearer',
    });
  }

  static changePassword(data) {
    const body = {
      ...data,
    };
    return request(API_URL_MAP.CHANGE_PASSWORD, {
      method: 'POST',
      auth: 'Bearer',
      body,
    });
  }

  static banUser(userId) {
    const url = `${API_URL_MAP.USERS}/${userId}${API_URL_MAP.BAN_USER}`;
    return request(url, {
      method: 'POST',
      auth: 'Bearer',
    });
  }

  static unlockUser(userId) {
    const url = `${API_URL_MAP.USERS}/${userId}${API_URL_MAP.UNLOCK_USER}`;
    return request(url, {
      method: 'POST',
      auth: 'Bearer',
    });
  }

  static addContactToFavorite(userId) {
    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.FAVORITE_CONTACTS}/${userId}`;
    return request(url, {
      method: 'PUT',
      auth: 'Bearer',
    });
  }

  static removeContactFromFavorite(userId) {
    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.FAVORITE_CONTACTS}/${userId}`;
    return request(url, {
      method: 'DELETE',
      auth: 'Bearer',
    });
  }

  static removeContact(userId) {
    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.CONTACTS}/${userId}`;
    return request(url, {
      method: 'DELETE',
      auth: 'Bearer',
    });
  }

  static loginWithSn({
    token, type, secretKey, redirectUri,
  }) {
    const body = {
      auth: {},
    };

    if (type === 'odnoklassniki' || type === 'facebook') {
      body.auth.accessToken = token;
    }

    if (type === 'odnoklassniki') {
      body.auth.sessionKey = secretKey;
    }

    if (type === 'vk' || type === 'mailRu') {
      body.auth.code = token;
      body.auth.redirectUri = redirectUri;
    }

    const url = `${API_URL_MAP.USERS + API_URL_MAP.SN_AUTH}/${type}`;

    return request(url, {
      method: 'POST',
      auth: 'Client',
      body,
    });
  }

  static linkAccountWithSn({
    token, type, secretKey, redirectUri,
  }) {
    const body = {
      auth: {},
    };

    if (type === 'odnoklassniki' || type === 'facebook') {
      body.auth.accessToken = token;
    }

    if (type === 'odnoklassniki') {
      body.auth.sessionKey = secretKey;
    }

    if (type === 'vk' || type === 'mailRu') {
      body.auth.code = token;
      body.auth.redirectUri = redirectUri;
    }

    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.LINKED_ACCOUNT}/${type}`;
    return request(url, {
      method: 'PUT',
      auth: 'Bearer',
      body,
    });
  }

  static unlinkAccountWithSn(type) {
    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.LINKED_ACCOUNT}/${type}`;
    return request(url, {
      method: 'DELETE',
      auth: 'Bearer',
    });
  }

  static updateUserSettings(data) {
    const body = {
      notifications: {
        ...data,
      },
    };

    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.UPDATE_USER_SETTINGS}`;
    return request(url, {
      method: 'PATCH',
      auth: 'Bearer',
      body,
    });
  }

  static updateCompanyPicture(photoId) {
    const body = {
      photoId,
    };

    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.COMPANY_PICTURE}`;
    return request(url, {
      method: 'PATCH',
      auth: 'Bearer',
      body,
    });
  }

  static updateResumePicture(photoId) {
    const body = {
      photoId,
    };

    const url = `${API_URL_MAP.USERS}/me${API_URL_MAP.RESUME_PICTURE}`;
    return request(url, {
      method: 'PATCH',
      auth: 'Bearer',
      body,
    });
  }

  static updateDeveloperSettings(data, userId) {
    const body = {
      ...data,
    };

    const url = `${API_URL_MAP.USERS}/${userId || 'me'}${API_URL_MAP.UPDATE_DEVELOPER_SETTINGS}`;
    return request(url, {
      method: 'PATCH',
      auth: 'Bearer',
      body,
    });
  }
}
